import TipsAndUpdates from "@mui/icons-material/TipsAndUpdates";
import { Alert, Box, DialogContentText, LinearProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { HoldingDialog } from "./holding-dialog";

import type { MultiformatConfig } from "../../modules/multiformat/store/types";
import type { FC, JSX } from "react";

export const ContentHoldingDialog: FC<{
  multiformatConfig: MultiformatConfig;
  pageReady: boolean;
  onDismiss?: () => void;
}> = ({ multiformatConfig, pageReady, onDismiss }) => {
  const [alertTextIndex, setAlertTextIndex] = useState(0);
  const [alertMinHeight, setAlertMinHeight] = useState<number | null>(null);
  const messageRotationTimingMs = 8000;

  const hasImages = multiformatConfig.layouts.some((layout) =>
    layout.images.some((image) => image.type),
  );

  const handleAlertContentRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    setAlertMinHeight((prev) =>
      prev === null ? node.offsetHeight : Math.max(prev, node.offsetHeight),
    );
  }, []);

  const alertMessages = useMemo(
    () => [
      "See an image you like in one template, but prefer the layout of another? Select 'Use this image on all templates' to see how it looks!",
      "You can change your imagery source by clicking 'Edit Prompt' at the top of the page",
    ],
    [],
  );

  useEffect(() => {
    if (!hasImages || multiformatConfig.name === "Video") return;

    const interval = setInterval(() => {
      setAlertTextIndex((prev) => (prev + 1) % alertMessages.length);
    }, messageRotationTimingMs);

    return () => clearInterval(interval);
  }, [alertMessages.length, hasImages, multiformatConfig.name]);

  return (
    <HoldingDialog
      pageReady={pageReady}
      dialogTitle={`We’re generating your ${multiformatConfig.name}s with AI`}
      continueButtonText="Continue to template selection"
      onDismiss={onDismiss}
    >
      <>
        <DialogContentText mb={2}>
          {hasImages
            ? `Please check the ${multiformatConfig.name === "Video" ? "video, " : ""}images and text we’ve generated, as AI results can vary.`
            : "Please check the assets we’ve generated, as AI results can vary."}
        </DialogContentText>
        <DialogContentText>
          Click on a design option to customize the text and layout of your chosen template.
        </DialogContentText>
        {hasImages && (
          <Alert
            icon={<GradientTipsAndUpdates />}
            sx={{
              my: 4,
              backgroundColor: "background.default",
              height: alertMinHeight ?? undefined,
              display: "flex",
              alignItems: "center",
            }}
          >
            <DialogContentText ref={handleAlertContentRef} sx={{ py: 1 }}>
              {alertMessages[alertTextIndex]}
            </DialogContentText>
          </Alert>
        )}
        <Box mt={4} display="flex" justifyContent="center">
          <LinearProgress variant="indeterminate" sx={{ width: "100%", maxWidth: 200 }} />
        </Box>
      </>
    </HoldingDialog>
  );
};

const GradientTipsAndUpdates = (): JSX.Element => (
  <>
    <svg width="0" height="0">
      <defs>
        <linearGradient id="linearColors" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00B6EF" />
          <stop offset="100%" stopColor="#C24599" />
        </linearGradient>
      </defs>
    </svg>

    <TipsAndUpdates
      sx={{
        fill: "url(#linearColors)",
      }}
    />
  </>
);
